/**
 * Authentication Context
 * Provides JWT token-based authentication state and methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getToken, isTokenValid, removeToken, getTokenPayload } from '../utils/jwt';
import apiService from '../services/api';

const AuthContext = createContext();

/**
 * Authentication Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Check authentication status using JWT token
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if we have a valid token locally
      const token = getToken();
      if (!token || !isTokenValid()) {
        setIsAuthenticated(false);
        setUserProfile(null);
        return;
      }

      // Validate token with server
      const authStatus = await apiService.checkAuthStatus();
      
      if (authStatus.authenticated) {
        setIsAuthenticated(true);
        
        // Fetch user profile if not already available
        if (!userProfile) {
          try {
            const profile = await apiService.getUserProfile();
            setUserProfile(profile);
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError);
            // Don't fail auth if profile fetch fails
          }
        }
      } else {
        setIsAuthenticated(false);
        setUserProfile(null);
      }
    } catch (err) {
      console.error('Auth status check failed:', err);
      setError('Failed to check authentication status');
      setIsAuthenticated(false);
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile]);

  /**
   * Handle login redirect
   */
  const login = useCallback(() => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    window.location.href = `${API_BASE_URL}/login`;
  }, []);

  /**
   * Handle logout
   */
  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (err) {
      console.error('Logout failed:', err);
      // Even if logout fails, clear local state
      setIsAuthenticated(false);
      setUserProfile(null);
      removeToken();
    }
  }, []);

  /**
   * Extract JWT token from URL and store it
   */
  const handleTokenFromUrl = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      try {
        // Store the token
        const { storeToken } = await import('../utils/jwt');
        storeToken(token);
        
        // Clean up URL by removing token parameter
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('token');
        window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
        
        // Check auth status with new token
        await checkAuthStatus();
        
        return true;
      } catch (error) {
        console.error('Failed to handle token from URL:', error);
        setError('Failed to process authentication token');
        return false;
      }
    }
    
    return false;
  }, [checkAuthStatus]);

  /**
   * Refresh user profile data
   */
  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const profile = await apiService.getUserProfile();
      setUserProfile(profile);
    } catch (err) {
      console.error('Failed to refresh user profile:', err);
      setError('Failed to refresh user profile');
    }
  }, [isAuthenticated]);

  /**
   * Get token information
   */
  const getTokenInfo = useCallback(() => {
    const token = getToken();
    if (!token) {
      return null;
    }

    const payload = getTokenPayload();
    return {
      token,
      payload,
      isExpired: !isTokenValid(),
    };
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // First, check if we have a logout parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isLogout = urlParams.get('logout') === 'true';
      
      if (isLogout) {
        // Handle logout from backend redirect
        console.log('Logout parameter detected, clearing local data');
        
        // Clear all authentication state immediately
        removeToken();
        setIsAuthenticated(false);
        setUserProfile(null);
        setError(null);
        setIsLoading(false);
        
        // Clean up URL by removing logout parameter
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('logout');
        window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
        
        console.log('Logout complete - user should see login page');
        return;
      }
      
      // Check if we have a token in the URL
      const tokenHandled = await handleTokenFromUrl();
      
      // If no token was handled from URL, check existing auth status
      if (!tokenHandled) {
        await checkAuthStatus();
      }
    };

    initializeAuth();
  }, [handleTokenFromUrl, checkAuthStatus]);

  // Set up token expiration monitoring
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkTokenExpiry = () => {
      if (!isTokenValid()) {
        console.log('Token expired, logging out');
        setIsAuthenticated(false);
        setUserProfile(null);
        removeToken();
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const contextValue = {
    // State
    isAuthenticated,
    isLoading,
    userProfile,
    error,
    
    // Actions
    login,
    logout,
    checkAuthStatus,
    refreshProfile,
    getTokenInfo,
    
    // Utilities
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
